import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { type DropdownMenuOption } from '@air/primitive-dropdown-menu';
import { useAirModal } from '@air/provider-modal';
import { useShortIdContext } from '@air/provider-short-id';
import { formatBytes } from '@air/utilities';
import { reportErrorToBugsnag } from '@air/utils-error';

import { useCopyToWorkspace } from '~/components/CopyToWorkspace/hooks/useCopyToWorkspace';
import { ExportImagesModal } from '~/components/ExportImagesModal/ExportImagesModal';
import { useCanViewAllVersionsOnPublicShareLink } from '~/hooks/useCanViewAllVersionsOnPublicShareLink';
import { useDownloadPublicAsset } from '~/hooks/useDownloadPublicAsset';
import {
  centralizedClipAssetIdSelector,
  centralizedClipAssetsSelector,
  centralizedClipDimensionsSelector,
  centralizedClipExtSelector,
  centralizedClipIdSelector,
  centralizedClipSizeSelector,
  centralizedDisplayTitleSelector,
} from '~/store/centralizedClip/selectors';
import { extCanBeExported, getBestImgixExportSource } from '~/utils/Imgix';
import { useAirStore } from '~/utils/ReduxUtils';

export const usePublicDownloadOptions = () => {
  const store = useAirStore();
  const { shortId } = useShortIdContext();
  const clipId = useSelector(centralizedClipIdSelector);
  const assetId = useSelector(centralizedClipAssetIdSelector);
  const ext = useSelector(centralizedClipExtSelector);
  const size = useSelector(centralizedClipSizeSelector);
  const [showExportImagesModal] = useAirModal(ExportImagesModal);
  const { copyToAir } = useCopyToWorkspace();
  const assets = useSelector(centralizedClipAssetsSelector);
  const { downloadPublicAsset } = useDownloadPublicAsset();

  const exportSource = getBestImgixExportSource(ext, assets);
  const { canViewAllVersions: canViewAssetVersions } = useCanViewAllVersionsOnPublicShareLink();

  const onSelectDownload = useMemo(
    () =>
      shortId && clipId
        ? () => {
            downloadPublicAsset({
              shortId,
              clipId,
              trackLocation: 'public-asset-page',
            }).catch((error) => {
              reportErrorToBugsnag({ error, context: 'Failed to download public asset' });
            });
          }
        : undefined,
    [clipId, downloadPublicAsset, shortId],
  );

  const onSelectExportModal = useMemo(
    () =>
      extCanBeExported(ext) && exportSource
        ? () => {
            const fileName = centralizedDisplayTitleSelector(store.getState());
            const { height, width } = centralizedClipDimensionsSelector(store.getState());

            return showExportImagesModal({
              fileName,
              height,
              imageSrc: exportSource,
              width,
            });
          }
        : undefined,
    [ext, exportSource, showExportImagesModal, store],
  );

  const onSelectCopy = useMemo(
    () => () => {
      /**
       * We only allow the entire asset to be copied if canViewAssetVersions
       * is true. Else, we just let them copy that single clip (asset version)
       */
      return canViewAssetVersions && assetId
        ? copyToAir({
            assetId,
            trackLocation: 'public-asset-page',
          })
        : copyToAir({
            clipId,
            trackLocation: 'public-asset-page',
          });
    },
    [assetId, canViewAssetVersions, clipId, copyToAir],
  );

  const options = useMemo(() => {
    const optionsArray: DropdownMenuOption[] = [];

    optionsArray.push({
      id: 'copy',
      label: 'Save a copy',
      description: 'Duplicate to an Air workspace',
      onSelect: onSelectCopy,
      type: 'item',
    });
    optionsArray.push({
      id: 'copy-separator',
      type: 'separator',
    });

    if (onSelectExportModal) {
      optionsArray.push({
        id: 'export',
        label: 'Export image',
        description: 'Adjust size and format',
        onSelect: onSelectExportModal,
        type: 'item',
      });
      optionsArray.push({
        id: 'export-separator',
        type: 'separator',
      });
    }

    if (onSelectDownload) {
      optionsArray.push({
        id: 'download',
        label: 'Original file',
        description: `${ext.toUpperCase()} · ${formatBytes(size)}`,
        onSelect: onSelectDownload,
        type: 'item',
      });
    }

    return optionsArray;
  }, [onSelectCopy, onSelectExportModal, ext, onSelectDownload, size]);

  return { options };
};
